import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { AvatarDisplay } from "./AvatarDisplay";
import { Trophy, UserPlus, Users, Zap, Lock } from "lucide-react";
import type { UserLookupResult } from "@/hooks/useUserLookup";
import { usePrivateRooms } from "@/hooks/usePrivateRooms";
import { useState } from "react";
import { ChallengeDialog } from "./ChallengeDialog";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";

interface PlayerCardProps {
  player: UserLookupResult & { isFake?: boolean; isPrivate?: boolean };
  showStats?: boolean;
  showActions?: boolean;
}

export function PlayerCard({ player, showStats = true, showActions = false }: PlayerCardProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { friends, sendFriendRequest, invitations } = usePrivateRooms();
  const [showChallengeDialog, setShowChallengeDialog] = useState(false);
  
  const winRate = player.games_played && player.games_won 
    ? Math.round((player.games_won / player.games_played) * 100)
    : 0;

  const isFriend = !player.isFake && friends.some(
    f => (f.requester_id === player.id || f.addressee_id === player.id) && f.status === 'accepted'
  );
  
  const hasPendingRequest = !player.isFake && friends.some(
    f => f.addressee_id === player.id && f.status === 'pending'
  );

  const handleCardClick = () => {
    // Only show private toast if isPrivate is true (not just isFake)
    if (player.isPrivate) {
      toast({
        title: "Private Profile",
        description: "This player's profile is set to private",
      });
      return;
    }
    navigate(`/player/${player.id}`);
  };

  const handleSendFriendRequest = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await sendFriendRequest(player.id);
      toast({
        title: "Friend Request Sent",
        description: `Friend request sent to ${player.username || player.display_name}`,
      });
    } catch (error) {
      console.error('Error sending friend request:', error);
      toast({
        title: "Error",
        description: "Could not send friend request",
        variant: "destructive",
      });
    }
  };

  const handleChallenge = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (player.isFake) {
      // Start a bot match with this fake player's name and ELO
      navigate(`/?botMatch=${encodeURIComponent(player.username || 'Bot')}&botElo=${player.elo_rating}`);
      return;
    }
    setShowChallengeDialog(true);
  };

  return (
    <Card 
      className="hover:shadow-lg transition-all cursor-pointer hover:border-primary/50 group"
      onClick={handleCardClick}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <AvatarDisplay
            username={player.username || player.display_name}
            avatarConfig={player.avatar_config}
            avatarUrl={player.avatar_url}
            size="md"
          />
          <div className="flex-1 min-w-0">
            <div className="font-semibold truncate group-hover:text-primary transition-colors flex items-center gap-1">
              {player.username || player.display_name || 'Anonymous'}
              {player.isPrivate && <Lock className="w-3 h-3 text-muted-foreground" />}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="secondary" className="gap-1">
                <Trophy className="w-3 h-3" />
                {player.elo_rating}
              </Badge>
              {isFriend && (
                <Badge variant="outline" className="gap-1">
                  <Users className="w-3 h-3" />
                  Friend
                </Badge>
              )}
              {showStats && player.games_played > 0 && (
                <span className="text-xs text-muted-foreground">
                  {player.games_played} games · {winRate}% wins
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        {showActions && user && user.id !== player.id && (
          <div className="flex gap-2 mt-3 pt-3 border-t" onClick={(e) => e.stopPropagation()}>
            {!isFriend && !hasPendingRequest && !player.isFake && (
              <Button 
                size="sm" 
                variant="outline"
                onClick={handleSendFriendRequest}
                className="flex-1"
              >
                <UserPlus className="w-3 h-3 mr-1" />
                Add
              </Button>
            )}
            
            <Button 
              size="sm" 
              variant="default"
              onClick={handleChallenge}
              className="flex-1"
            >
              <Zap className="w-3 h-3 mr-1" />
              Challenge
            </Button>
          </div>
        )}
      </CardContent>

      {/* Challenge Dialog */}
      <ChallengeDialog
        targetUserId={player.id}
        targetUsername={player.username || player.display_name || 'Player'}
        targetElo={player.elo_rating}
        isFriend={isFriend}
        open={showChallengeDialog}
        onOpenChange={setShowChallengeDialog}
      />
    </Card>
  );
}
